import { useState, useEffect } from 'react'
import { useSettings, useUpdateSettings, type KBSettings } from '@/hooks/useSettings'
import { QuickTemplates } from './QuickTemplates'
import { CustomAgentSettings } from './CustomAgentSettings'
import { ParserSettingsCard } from './ParserSettingsCard'
import { PrivacySettingsCard } from './PrivacySettingsCard'
import { DangerZone } from './DangerZone'
import { Loader2, AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useReparseAllDocuments } from '@/hooks/useTagsets'
import { useToast } from '@/hooks/use-toast'

interface SettingsTabProps {
  kbId: string
  kbName: string
  needsReparse?: boolean
  reparseReason?: string
}

export function SettingsTab({ kbId, kbName, needsReparse, reparseReason }: SettingsTabProps) {
  const { toast } = useToast()
  const reparseAll = useReparseAllDocuments(kbId)
  const { data: settings, isLoading } = useSettings(kbId)
  const updateSettings = useUpdateSettings(kbId)
  const [formData, setFormData] = useState<KBSettings | undefined>(settings)

  // Update form data when settings change
  useEffect(() => {
    if (settings) {
      setFormData(settings)
    }
  }, [settings])
  
  const handleReparseAll = async () => {
    try {
      const result = await reparseAll.mutateAsync()
      toast({
        title: 'Reparsing started',
        description: result.message,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to start reparsing',
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof KBSettings, value: any) => {
    setFormData(prev => prev ? { ...prev, [field]: value } : prev)
  }

  const handleSave = async () => {
    if (!formData) return
    try {
      await updateSettings.mutateAsync(formData)
      toast({
        title: 'Settings saved',
        description: 'Your settings have been updated successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!settings || !formData) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Failed to load settings
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Reparse Alert Banner */}
      {needsReparse && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-semibold text-yellow-900">Reparsing Required</p>
              <p className="text-sm text-yellow-800 mt-1">{reparseReason}</p>
            </div>
            <Button
              size="sm"
              onClick={handleReparseAll}
              disabled={reparseAll.isPending}
              className="ml-4"
            >
              {reparseAll.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reparsing...
                </>
              ) : (
                'Reparse All Documents Now'
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Quick Templates */}
      <QuickTemplates kbId={kbId} />

      {/* Custom Agent Settings */}
      <CustomAgentSettings formData={formData} handleChange={handleChange} />

      {/* Settings - Single Column Stack */}
      <ParserSettingsCard formData={formData} handleChange={handleChange} />
      
      <PrivacySettingsCard formData={formData} handleChange={handleChange} />

      {/* Save Button - Sticky at bottom */}
      <div className="sticky bottom-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t p-4 -mx-6">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <p className="text-sm text-muted-foreground">
            Make changes above and click save to apply them
          </p>
          <Button
            onClick={handleSave}
            disabled={updateSettings.isPending || (formData.agent && !formData.agent_id.trim())}
            size="lg"
            className="min-w-[150px]"
          >
            {updateSettings.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save All Settings'
            )}
          </Button>
        </div>
      </div>

      {/* Danger Zone */}
      <DangerZone kbId={kbId} kbName={kbName} />
    </div>
  )
}
